import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllTransfers } from '../../services/Transfer/transferService';
import { transferReportPdf } from '../../services/Reports/reportsService';
import CreateTransferModal from './CreateTransferModal';
import EditTransferModal from './EditTransferModal';
import TransferFilter from '../Filters/TransferFilter';
import { formatDate, getCurrentDate, getCurrentDateMinusDays } from '../Utils/utils';
import { FaEdit, FaPrint } from 'react-icons/fa';
import { Operations } from '../Enum/OperationsEnum';
import { usePinVerification } from '../../context/PinVerificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Transfer = () => {
  const { requirePinVerification } = usePinVerification();
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setTransfers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateTransferModal, setShowCreateTransferModal] = useState(false);
  const [showEditTransferModal, setShowEditTransferModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('transferView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('transferCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('transferUpdate'), [hasPermission, permissions]);
  const defaultFilters = {
    initialDate: getCurrentDateMinusDays(5),
    finalDate: getCurrentDate(),
    active: true
  };

  const [filterParams, setFilterParams] = useState(defaultFilters);
  const fetchTransfers = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllTransfers(filters, currentPage - 1, currentPageSize);
      setTransfers(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchTransfers();
    }
  }, [page, pageSize, fetchTransfers, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateTransferModal = async () => {
    if (!checkPermission('transferCreate')) {
      return;
    }
    try {
      await requirePinVerification();
      setShowCreateTransferModal(true);
    } catch (error) {
      console.error('Erro ao verificar PIN:', error);
    }
  };

  const handleCloseCreateTransferModal = () => setShowCreateTransferModal(false);

  const handlePrintTransfer = (transfer) => {
    transferReportPdf(transfer.id);
  };

  const handleOpenEditTransferModal = async (transfer) => {
    if (!checkPermission('transferUpdate')) {
      return;
    }
    try {
      await requirePinVerification();
      setSelectedTransfer(transfer);
      setShowEditTransferModal(true);
    } catch (error) {
      console.error('Erro ao verificar PIN:', error);
    }
  };
  
  const handleCloseEditTransferModal = () => {
    setSelectedTransfer(null);
    setShowEditTransferModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchTransfers(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setPage(1);
    fetchTransfers(defaultFilters, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Transferências</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Transferências</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateTransferModal}>
              Nova Transferência
            </Button>
          )}
        </Col>
      </Row>
      {canView && <TransferFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Código</th>
              <th>Data</th>
              <th>Origem</th>
              <th>Destino</th>
              <th>Operação</th>
              <th>Produto</th>
              <th>Quantidade</th>
              <th style={{textAlign: 'center', width: '70px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((transfer) => (
              <tr key={transfer.id}>
                <td>{transfer.oldId}</td>
                <td>{formatDate(transfer.transferDate)}</td>
                <td>{transfer.originPerson.oldId + ' - ' + transfer.originPerson.name}</td>
                <td>{transfer?.destinyPerson ? transfer.destinyPerson.oldId + ' - ' + transfer.destinyPerson.name : ''}</td>
                <td>{Operations[transfer.type]}</td>
                <td>{transfer.product.description}</td>
                <td>{transfer.quantity}</td>
                <td style={{textAlign: 'center'}}>
                  <Button
                    variant="link"
                    onClick={() => handlePrintTransfer(transfer)}
                    className="p-0"
                    style={{ fontSize: '1.2rem' }}
                    title="Imprimir"
                  >
                    <FaPrint className='icon-custom'/>
                  </Button>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditTransferModal(transfer)}
                      className="p-0"
                      style={{ fontSize: '1.2rem', marginLeft: '10px' }}
                      title="Visualizar"
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateTransferModal
        show={showCreateTransferModal}
        handleClose={handleCloseCreateTransferModal}
        refreshTransfers={fetchTransfers}
      />
      <EditTransferModal
        show={showEditTransferModal}
        handleClose={handleCloseEditTransferModal}
        transfer={selectedTransfer}
        refreshTransfers={fetchTransfers}
      />
    </Container>
  );
};

export default Transfer;